import { useEffect } from "react";

/**
 * usePortalPWA
 * ------------
 * Point the browser at the *customer portal* manifest so the "Add to Home
 * Screen" prompt shows a customer-branded name/icon distinct from the staff
 * dispatch console. Also injects the iOS-specific meta tags that make the
 * app open in true standalone mode (no address bar, no navigation chrome).
 *
 * Runs on mount, cleans up on unmount so the operator console reverts to its
 * own manifest when the user navigates away from /portal/*.
 */
export default function usePortalPWA() {
  useEffect(() => {
    const head = document.head;
    // 1 · swap <link rel="manifest">
    const manifestLink = head.querySelector('link[rel="manifest"]');
    const prevManifest = manifestLink?.getAttribute("href");
    if (manifestLink) manifestLink.setAttribute("href", "/portal.webmanifest");

    // 2 · theme-color meta (address-bar tint on Chrome / Android)
    let themeMeta = head.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta?.getAttribute("content");
    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.setAttribute("name", "theme-color");
      head.appendChild(themeMeta);
    }
    themeMeta.setAttribute("content", "#F57C00");

    // 3 · iOS standalone hints
    const inject = (name, content) => {
      let m = head.querySelector(`meta[name="${name}"]`);
      const prev = m?.getAttribute("content");
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", name);
        head.appendChild(m);
      }
      m.setAttribute("content", content);
      return { m, prev };
    };
    const capable = inject("apple-mobile-web-app-capable", "yes");
    const status = inject("apple-mobile-web-app-status-bar-style", "black-translucent");
    const title = inject("apple-mobile-web-app-title", "ArmorBatch Portal");

    // 4 · apple-touch-icon override (default in index.html is fine, but pin it)
    let apple = head.querySelector('link[rel="apple-touch-icon"]');
    const prevApple = apple?.getAttribute("href");
    if (!apple) {
      apple = document.createElement("link");
      apple.setAttribute("rel", "apple-touch-icon");
      head.appendChild(apple);
    }
    apple.setAttribute("href", "/icons/icon-180.png");

    // Cleanup — restore whatever the operator console had before.
    return () => {
      if (manifestLink && prevManifest) manifestLink.setAttribute("href", prevManifest);
      if (themeMeta && prevTheme != null) themeMeta.setAttribute("content", prevTheme);
      if (capable.m && capable.prev != null) capable.m.setAttribute("content", capable.prev);
      if (status.m && status.prev != null) status.m.setAttribute("content", status.prev);
      if (title.m && title.prev != null) title.m.setAttribute("content", title.prev);
      if (apple && prevApple) apple.setAttribute("href", prevApple);
    };
  }, []);
}
